import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMobileLayout } from '../../hooks/useMobileLayout';
import { useTouchTarget, getTouchTargetStyles } from '../../hooks/useTouchTarget';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Checkbox,
  Stack,
  Pagination,
  FormControlLabel,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  SmartToy as AIIcon,
  Archive as ArchiveIcon
} from '@mui/icons-material';
// import ISDOCDownload from '../ISDOC/ISDOCDownload'; // Temporarily disabled
import PDFDownload from '../PDF/PDFDownload';
// Optimized table with virtualization for better Safari performance
import OptimizedInvoiceTable from './OptimizedInvoiceTable';
import InternalNotesDialog from './InternalNotesDialog';
import AIInvoiceUpload from './AIInvoiceUpload';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { formatDateSafe } from '../../utils/dateUtils';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

import { 
  fetchInvoices, 
  fetchInvoiceById, 
  updateInvoice, 
  updateInvoiceProcessingStatus, 
  deleteInvoice, 
  bulkDeleteInvoices, 
  bulkUpdateInvoiceStatus, 
  archiveInvoice,
  bulkArchiveInvoices,
  archiveInvoicesByStatus,
  setPagination,
  getReadyInvoices
} from '../../store/slices/invoiceSlice';
import { RootState, AppDispatch } from '../../store/store';
import { 
  selectInvoices, 
  selectInvoicesLoading, 
  selectInvoicesError, 
  selectInvoicesPagination,
  selectFilteredInvoices,
  selectOptimizedInvoices
} from '../../store/selectors/invoiceSelectors';
import { generateABOFile, generateAndSubmitABOFile } from '../../store/slices/aboSlice';
import { Invoice } from '../../types/invoice';
import api from '../../config/axios';
import czechTranslations from '../../translations/czech';
import useModernAlert from '../../hooks/useModernAlert';
import '../../styles/universal-performance.css';
import { useBusinessUnits } from '../../hooks/useBusinessUnits';
import { useOptimizedDataFetching } from '../../hooks/useOptimizedDataFetching';
import CompanyAssignmentDialog from './CompanyAssignmentDialog';
import ExportButton from '../Export/ExportButton';

const statusColors = {
  new: 'default',
  pending: 'warning',
  reviewed: 'info',
  ready_for_payment: 'primary',
  sent_to_bank: 'secondary',
  paid: 'success',
  paid_in_cash: 'success',
  rejected: 'error',
  archived: 'default'
} as const;

// Use translations for status labels
const getStatusLabel = (status: string) => {
  return czechTranslations.invoices.status[status as keyof typeof czechTranslations.invoices.status] || status;
};

const aboStatusLabels = {
  generated: 'Vytvořeno',
  sent: 'Odesláno',
  sent_to_fio: 'Odesláno do FIO',
  error: 'Chyba'
} as const;

const Invoices: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { showError, showSuccess, AlertComponent } = useModernAlert();
  // Optimized selectors for all browsers - Safari performance optimized
  const invoices = useSelector(selectInvoices);
  const loading = useSelector(selectInvoicesLoading);
  const error = useSelector(selectInvoicesError);
  const pagination = useSelector(selectInvoicesPagination);
  const { user } = useSelector((state: RootState) => state.auth);
  
  // Use optimized invoices selector for better Safari performance
  const optimizedInvoices = useSelector((state: RootState) => selectOptimizedInvoices(state, pagination.limit));

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [invoiceToArchive, setInvoiceToArchive] = useState<Invoice | null>(null);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);
  const [archiveByStatusDialogOpen, setArchiveByStatusDialogOpen] = useState(false);
  const [statusToArchive, setStatusToArchive] = useState<string>('');
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [bulkStatusToUpdate, setBulkStatusToUpdate] = useState<string>('');
  const [bulkExportLoading, setBulkExportLoading] = useState(false);
  const [companyAssignmentDialogOpen, setCompanyAssignmentDialogOpen] = useState(false);
  const [invoiceToAssignCompany, setInvoiceToAssignCompany] = useState<Invoice | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [createABODialogOpen, setCreateABODialogOpen] = useState(false);
  const [selectedABOInvoices, setSelectedABOInvoices] = useState<number[]>([]);
  const [selectedABOBankType, setSelectedABOBankType] = useState<string>('FIO_ABO');
  const [submitNow, setSubmitNow] = useState(false);
  const [immediateBankAccountId, setImmediateBankAccountId] = useState<number | null>(null);
  const [availableImmediateBankAccounts, setAvailableImmediateBankAccounts] = useState<any[]>([]);
  const [loadingImmediateBankAccounts, setLoadingImmediateBankAccounts] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedInvoiceForNotes, setSelectedInvoiceForNotes] = useState<Invoice | null>(null);
  const [creatingABO, setCreatingABO] = useState(false);
  const [aiUploadDialogOpen, setAiUploadDialogOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25, // 🚀 Optimized page size for Safari performance
  });

  // Business units hook
  const { businessUnits, loading: businessUnitsLoading, error: businessUnitsError } = useBusinessUnits();
  
  // Optimized data fetching hook for Safari performance
  const { refreshData: optimizedRefreshData } = useOptimizedDataFetching({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    statusFilter,
    businessUnitFilter,
    searchTerm,
    dateFrom,
    dateTo,
  });
  

  
  // Auto-select business unit if only one is available
  useEffect(() => {
    if (businessUnits && businessUnits.length === 1 && businessUnitFilter === 'all') {
      setBusinessUnitFilter(businessUnits[0].id.toString());
    }
  }, [businessUnits, businessUnitFilter]);
  

  
  // 🔄 Data refresh function - optimized for Safari performance
  const refreshData = useCallback(() => {
    optimizedRefreshData();
  }, [optimizedRefreshData]);

  // Handle sorting
  const handleSortChange = useCallback((sortInfo: { field: string; direction: 'asc' | 'desc' }) => {
    setSortConfig(sortInfo);
    // You can implement server-side sorting here if needed
    console.log('Sorting by:', sortInfo);
  }, []);

  // Data fetching is now handled by the optimized hook with debouncing

  // 🔄 Refresh data when navigating back to invoices - FIXED: Removed refreshData from dependencies to prevent infinite loop
  useEffect(() => {
    if (location.pathname === '/invoices') {
      const params: any = {
        page: paginationModel.page + 1, 
        limit: paginationModel.pageSize,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        businessUnitId: businessUnitFilter !== 'all' ? businessUnitFilter : undefined,
        search: searchTerm || undefined
      };
      
      // Add date filters - with validation
      if (dateFrom && dateFrom instanceof Date && !isNaN(dateFrom.getTime())) {
        params.dateFrom = format(dateFrom, 'yyyy-MM-dd');
      }
      if (dateTo && dateTo instanceof Date && !isNaN(dateTo.getTime())) {
        params.dateTo = format(dateTo, 'yyyy-MM-dd');
      }
      
      dispatch(fetchInvoices(params));
    }
  }, [location.pathname, dispatch, paginationModel.page, paginationModel.pageSize, statusFilter, businessUnitFilter, searchTerm, dateFrom, dateTo]);

  // 🎯 Scroll position preservation
  const [scrollPosition, setScrollPosition] = useState(0);
  const [tableScrollPosition, setTableScrollPosition] = useState(0);
  
  // Save scroll position before refresh
  const saveScrollPosition = useCallback(() => {
    setScrollPosition(window.scrollY);
    // Save table scroll position if available
    const tableElement = document.querySelector('.MuiDataGrid-root');
    if (tableElement) {
      setTableScrollPosition(tableElement.scrollTop || 0);
    }
  }, []);

  // Restore scroll position after data update
  useEffect(() => {
    if (scrollPosition > 0) {
      window.scrollTo(0, scrollPosition);
    }
    if (tableScrollPosition > 0) {
      const tableElement = document.querySelector('.MuiDataGrid-root');
      if (tableElement) {
        tableElement.scrollTop = tableScrollPosition;
      }
    }
  }, [invoices, scrollPosition, tableScrollPosition]);

  // Fetch bank accounts when submitNow changes
  useEffect(() => {
    if (submitNow && selectedABOBankType === 'FIO_API' && businessUnitFilter !== 'all') {
      fetchImmediateBankAccounts();
    } else {
      setAvailableImmediateBankAccounts([]);
      setImmediateBankAccountId(null);
    }
  }, [submitNow, selectedABOBankType, businessUnitFilter]);

  const loadInvoices = useCallback(() => {
    const params: any = {
      page: paginationModel.page + 1, // Server uses 1-based pagination
      limit: paginationModel.pageSize,
    };

    if (searchTerm) params.search = searchTerm;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (businessUnitFilter !== 'all') params.businessUnitId = businessUnitFilter;
    
    // Add date filters (VAT rendered date) - with validation
    if (dateFrom && dateFrom instanceof Date && !isNaN(dateFrom.getTime())) {
      params.dateFrom = format(dateFrom, 'yyyy-MM-dd');
    }
    if (dateTo && dateTo instanceof Date && !isNaN(dateTo.getTime())) {
      params.dateTo = format(dateTo, 'yyyy-MM-dd');
    }
    dispatch(fetchInvoices(params));
  }, [dispatch, paginationModel, searchTerm, statusFilter, businessUnitFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);
  
  // Update indeterminate property when selection changes
  useEffect(() => {
    if (selectAllRef.current) {
      const isIndeterminate = selectedInvoices.length > 0 && selectedInvoices.length < invoices.length;
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [selectedInvoices, invoices]);
  
  // Ensure businessUnits is always an array
  const safeBusinessUnits = Array.isArray(businessUnits) ? businessUnits : [];

  // 🚀 Memoized statistics calculations to prevent recalculation on every render
  const statistics = useMemo(() => {
    const total = invoices.length;
    const newCount = invoices.filter(i => i.status === 'new').length;
    const pendingCount = invoices.filter(i => i.status === 'pending').length;
    const readyForPaymentCount = invoices.filter(i => i.status === 'ready_for_payment').length;
    const totalValue = invoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);

    return {
      total,
      new: newCount,
      pending: pendingCount,
      readyForPayment: readyForPaymentCount,
      totalValue
    };
  }, [invoices]);

  // Selection handlers for TanStack Table
  const handleSelectionChange = useCallback((newSelection: number[]) => {
    setSelectedInvoices(newSelection);
  }, []);

  const handleInvoiceSelect = useCallback((id: number, selected: boolean) => {
    if (selected) {
      setSelectedInvoices(prev => [...prev, id]);
    } else {
      setSelectedInvoices(prev => prev.filter(invoiceId => invoiceId !== id));
    }
  }, []);

  const handleView = useCallback((id: number) => {
    navigate(`/invoices/${id}`);
  }, [navigate]);

  const handleEdit = useCallback((id: number) => {
    navigate(`/invoices/${id}/edit`);
  }, [navigate]);

  const handleAssignCompany = useCallback((invoice: Invoice) => {
    setInvoiceToAssignCompany(invoice);
    setCompanyAssignmentDialogOpen(true);
  }, []);

  const handleDeleteClick = useCallback((invoice: Invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteDialogOpen(true);
  }, []);

  // Wrapper for VirtualizedInvoiceList that expects (id: number) => void
  const handleDeleteById = useCallback((id: number) => {
    const invoice = invoices.find(inv => inv.id === id);
    if (invoice) {
      handleDeleteClick(invoice);
    }
  }, [invoices, handleDeleteClick]);

  // TanStack Table columns are now defined in TanStackInvoiceTable component
  
  // Show loading state while business units are loading
  if (businessUnitsLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Načítání firem...</Typography>
      </Box>
    );
  }
  
  // Show error state if business units failed to load
  if (businessUnitsError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Chyba při načítání firem: {businessUnitsError}
        </Alert>
        <Button onClick={() => window.location.reload()}>
          Obnovit stránku
        </Button>
      </Box>
    );
  }

  const handleStatusChange = async (invoiceId: number, newStatus: string) => {
    try {
      console.log(`🔄 Updating invoice ${invoiceId} status to: ${newStatus}`);
      
      // Save current scroll position before update
      saveScrollPosition();
      
      const result = await dispatch(updateInvoiceProcessingStatus({ id: invoiceId, processingStatus: newStatus })).unwrap();
      console.log(`✅ Status update successful for invoice ${invoiceId}:`, result);
      
      // Force a complete data refresh to ensure UI is in sync
      await dispatch(fetchInvoices({}));
      
      // Show success message
      showSuccess(`Status faktury úspěšně aktualizován na: ${getStatusLabel(newStatus)}`);
    } catch (error) {
      console.error(`❌ Status update error for invoice ${invoiceId}:`, error);
      
      // Show detailed error message
      let errorMessage = 'Chyba při aktualizaci statusu faktury';
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage += `: ${error.message}`;
      } else if (typeof error === 'string') {
        errorMessage += `: ${error}`;
      }
      
      showError(errorMessage);
      
      // Force refresh data to ensure UI shows correct state
      await dispatch(fetchInvoices({}));
    }
  };

  const handleDeleteConfirm = async () => {
    if (invoiceToDelete) {
      try {
        await dispatch(deleteInvoice(invoiceToDelete.id)).unwrap();
        loadInvoices();
        setDeleteDialogOpen(false);
        setInvoiceToDelete(null);
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setInvoiceToDelete(null);
  };

  const handleBulkDeleteClick = () => {
    if (selectedInvoices.length > 0) {
      setBulkDeleteDialogOpen(true);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      await dispatch(bulkDeleteInvoices(selectedInvoices)).unwrap();
      setSelectedInvoices([]);
      setBulkDeleteDialogOpen(false);
      loadInvoices();
      // Show success message
      showSuccess(`Úspěšně smazáno ${selectedInvoices.length} faktur`);
    } catch (error: any) {
      console.error('Bulk delete error:', error);
      // Show error to user
      showError(`${czechTranslations.alerts.deleteInvoicesError}: ${error?.message || czechTranslations.alerts.unknownError}`);
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleArchiveClick = (invoice: Invoice) => {
    setInvoiceToArchive(invoice);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!invoiceToArchive) return;
    
    try {
      await dispatch(archiveInvoice(invoiceToArchive.id)).unwrap();
      setArchiveDialogOpen(false);
      setInvoiceToArchive(null);
      loadInvoices();
      showSuccess(czechTranslations.invoices.messages.archiveSuccess);
    } catch (error: any) {
      console.error('Archive error:', error);
      showError(`${czechTranslations.invoices.messages.archiveError}: ${error?.message || czechTranslations.alerts.unknownError}`);
    }
  };

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
    setInvoiceToArchive(null);
  };

  const handleBulkArchiveClick = () => {
    if (selectedInvoices.length > 0) {
      setBulkArchiveDialogOpen(true);
    }
  };

  const handleBulkArchiveConfirm = async () => {
    try {
      await dispatch(bulkArchiveInvoices(selectedInvoices)).unwrap();
      setSelectedInvoices([]);
      setBulkArchiveDialogOpen(false);
      loadInvoices();
      showSuccess(czechTranslations.invoices.messages.bulkArchiveSuccess(selectedInvoices.length));
    } catch (error: any) {
      console.error('Bulk archive error:', error);
      showError(`${czechTranslations.invoices.messages.bulkArchiveError}: ${error?.message || czechTranslations.alerts.unknownError}`);
    }
  };

  const handleBulkArchiveCancel = () => {
    setBulkArchiveDialogOpen(false);
  };

  const handleArchiveByStatusClick = () => {
    setArchiveByStatusDialogOpen(true);
  };

  const handleArchiveByStatusConfirm = async () => {
    if (!statusToArchive) {
      showError('Vyberte prosím stav');
      return;
    }

    try {
      const result = await dispatch(archiveInvoicesByStatus(statusToArchive)).unwrap();
      setArchiveByStatusDialogOpen(false);
      setStatusToArchive('');
      loadInvoices();
      showSuccess(`Úspěšně archivováno ${result.archivedCount} faktur se stavem "${getStatusLabel(statusToArchive)}"`);
    } catch (error: any) {
      console.error('Archive by status error:', error);
      showError(`${czechTranslations.invoices.messages.bulkArchiveError}: ${error?.message || czechTranslations.alerts.unknownError}`);
    }
  };

  const handleArchiveByStatusCancel = () => {
    setArchiveByStatusDialogOpen(false);
    setStatusToArchive('');
  };

  const handleBulkStatusChange = async () => {
    if (bulkStatusToUpdate) {
      try {
        // Save current scroll position before update
        saveScrollPosition();
        
        const result = await dispatch(bulkUpdateInvoiceStatus({ ids: selectedInvoices, status: bulkStatusToUpdate })).unwrap();
        
        showSuccess(`Úspěšně aktualizováno ${selectedInvoices.length} faktur na status: ${getStatusLabel(bulkStatusToUpdate)}`);
        
        // Clear selection and refresh data
        setSelectedInvoices([]);
        setBulkStatusToUpdate('');
        setBulkStatusDialogOpen(false);
        
        // Force a complete data refresh to ensure UI is in sync
        await dispatch(fetchInvoices({}));
      } catch (error) {
        console.error(`❌ Bulk status update error:`, error);
        
        // Show detailed error message
        let errorMessage = 'Chyba při hromadné aktualizaci statusu faktur';
        if (error && typeof error === 'object' && 'message' in error) {
          errorMessage += `: ${error.message}`;
        } else if (typeof error === 'string') {
          errorMessage += `: ${error}`;
        }
        
        showError(errorMessage);
        
        // Force refresh data to ensure UI shows correct state
        await dispatch(fetchInvoices({}));
      }
    }
  };

  // Bulk ISDOC export handler
  const handleBulkISDOCExport = async () => {
    if (selectedInvoices.length === 0) {
      showError('Vyberte alespoň jednu fakturu');
      return;
    }

    setBulkExportLoading(true);
    try {
      console.log(`Exporting ${selectedInvoices.length} invoices to ISDOC...`);
      
      const response = await api.post('/isdoc/bulk-export', {
        invoiceIds: selectedInvoices
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ISDOC_bulk_${new Date().toISOString().slice(0, 10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(`Úspěšně exportováno ${selectedInvoices.length} faktur do ISDOC formátu`);
      
      // Clear selection and refresh
      setSelectedInvoices([]);
      await dispatch(fetchInvoices({}));
    } catch (error: any) {
      console.error('Bulk ISDOC export error:', error);
      showError(`Chyba při exportu ISDOC: ${error?.message || 'Neznámá chyba'}`);
    } finally {
      setBulkExportLoading(false);
    }
  };

  const handleBulkStatusCancel = () => {
    setBulkStatusDialogOpen(false);
  };

  // Create ABO helper functions
  const fetchImmediateBankAccounts = async () => {
    setLoadingImmediateBankAccounts(true);
    try {
      const response = await api.get(`/abo/fio/bank-accounts?businessUnitId=${businessUnitFilter}`);
      
      const fioAccounts = response.data.bankAccounts.filter((account: any) => account.fio_token);
      
      setAvailableImmediateBankAccounts(fioAccounts);
      
      // Auto-select the first available account
      if (fioAccounts.length > 0) {
        setImmediateBankAccountId(fioAccounts[0].id);
      } else {
        setImmediateBankAccountId(null);
      }
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
      showError('Nepodařilo se načíst bankovní účty');
    } finally {
      setLoadingImmediateBankAccounts(false);
    }
  };

  const handleCreateABO = async () => {
    if (selectedABOInvoices.length === 0) return;

    // Validate bank account selection for immediate submission
    if (submitNow && selectedABOBankType === 'FIO_API' && !immediateBankAccountId) {
      showError('Musíte vybrat bankovní účet pro okamžité odeslání');
      return;
    }

    setCreatingABO(true);
    try {
      if (submitNow && selectedABOBankType === 'FIO_API') {
        // Use the combined action with bank account selection
        await dispatch(generateAndSubmitABOFile({
          invoiceIds: selectedABOInvoices,
          bankType: selectedABOBankType,
          businessUnitId: parseInt(businessUnitFilter),
          submitNow: true,
          bankAccountId: immediateBankAccountId!
        })).unwrap();
        
        showSuccess('Platební soubor byl vytvořen a odeslán do FIO banky');
      } else {
        // Use existing generation logic
        await dispatch(generateABOFile({
          invoiceIds: selectedABOInvoices,
          bankType: selectedABOBankType,
          businessUnitId: parseInt(businessUnitFilter)
        })).unwrap();
        
        showSuccess('Platební soubor byl úspěšně vytvořen');
      }

      setCreateABODialogOpen(false);
      setSelectedABOInvoices([]);
      setSelectedABOBankType('FIO_ABO');
      setSubmitNow(false);
      setImmediateBankAccountId(null);
      setAvailableImmediateBankAccounts([]);
      
      // Refresh data with current filters (non-debounced)
      loadInvoices();
    } catch (error: any) {
      console.error('ABO creation error:', error);
      
      // Handle structured error responses (error is now the full data object from Redux)
      const errorData = typeof error === 'object' && error !== null ? error : null;
      
      if (errorData && errorData.type) {
        // Structured error from backend
        if (errorData.type === 'timeout_error') {
          showError(`⏱️ ${errorData.error}`);
        } else if (errorData.type === 'bank_validation_error') {
          const details = errorData.details || errorData.error;
          showError(`❌ Validace bankovních účtů\n\n${details}`);
        } else if (errorData.type === 'fio_api_error') {
          let errorMessage = `❌ Chyba FIO API\n\n${errorData.error || 'Neznámá chyba'}`;
          
          if (errorData.errorCode) {
            errorMessage += `\n\nKód chyby: ${errorData.errorCode}`;
          }
          
          if (errorData.detailedErrors && Array.isArray(errorData.detailedErrors) && errorData.detailedErrors.length > 0) {
            errorMessage += '\n\nChyby u jednotlivých plateb:';
            errorData.detailedErrors.forEach((err: any) => {
              errorMessage += `\n• VS ${err.vs} (účet ${err.account}/${err.bankCode}): ${err.error}`;
            });
          }
          
          showError(errorMessage);
        } else if (errorData.type === 'no_invoices') {
          showError('❌ Žádné faktury nejsou připraveny k platbě');
        } else if (errorData.type === 'validation_error') {
          const details = errorData.details || errorData.error;
          showError(`❌ Chyba validace\n\n${details}`);
        } else {
          showError(`❌ ${errorData.error || 'Chyba při vytváření platebního souboru'}`);
        }
      } else {
        // Fallback for string errors or unexpected format
        const errorMessage = typeof error === 'string' ? error : 
                            errorData?.error || 
                            error?.message || 
                            'Chyba při vytváření platebního souboru';
        showError(errorMessage);
      }
    } finally {
      setCreatingABO(false);
    }
  };

  const handleCloseABODialog = () => {
    setCreateABODialogOpen(false);
    setSelectedABOInvoices([]);
    setSelectedABOBankType('FIO_ABO');
    setSubmitNow(false);
    setImmediateBankAccountId(null);
    setAvailableImmediateBankAccounts([]);
  };

  const handleABOInvoiceToggle = (invoiceId: number) => {
    setSelectedABOInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleSelectAllReadyInvoices = () => {
    const readyInvoices = invoices.filter(i => i.status === 'ready_for_payment');
    if (selectedABOInvoices.length === readyInvoices.length) {
      setSelectedABOInvoices([]);
    } else {
      setSelectedABOInvoices(readyInvoices.map(invoice => invoice.id));
    }
  };

  // Check if we're in bulk delete mode (for mobile)
  const bulkDeleteMode = selectedInvoices.length > 0;

  const handlePaginationModelChange = (newModel: { page: number; pageSize: number }) => {
    setPaginationModel(newModel);
  };

  // Internal notes handlers
  const handleOpenNotesDialog = (invoice: Invoice) => {
    setSelectedInvoiceForNotes(invoice);
    setNotesDialogOpen(true);
  };

  const handleCloseNotesDialog = () => {
    setNotesDialogOpen(false);
    setSelectedInvoiceForNotes(null);
  };

  const handleSaveInternalNotes = async (invoiceId: number, notes: string) => {
    try {
      await api.patch(`/invoices/${invoiceId}/internal-notes`, {
        internalNotes: notes
      });
      
      showSuccess(czechTranslations.invoices.internalNotes.updated);
      
      // Refresh invoice list to show updated note indicator
      await loadInvoices();
    } catch (error: any) {
      console.error('Failed to save internal notes:', error);
      showError(czechTranslations.invoices.internalNotes.updateFailed + ': ' + (error.response?.data?.error || error.message));
      throw error; // Re-throw so dialog can handle loading state
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2, lg: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', lg: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', lg: 'center' }, 
        mb: { xs: 2, md: 2, lg: 3 },
        gap: { xs: 1.5, md: 2 }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 0 }, width: '100%' }}>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '1.75rem', lg: '2rem' } }}>
            Faktury
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 1, md: 1.5 }, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                saveScrollPosition(); // Save position before manual refresh
                loadInvoices();
              }}
              disabled={loading}
              sx={{ minWidth: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.75rem', md: '0.8rem' } }}
              size={isTablet ? 'small' : 'medium'}
            >
              {loading ? 'Načítání...' : 'Obnovit'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/invoices/new')}
              sx={{ minWidth: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.75rem', md: '0.8rem' } }}
              size={isTablet ? 'small' : 'medium'}
            >
              Přidat fakturu
            </Button>
            
            {/* AI Upload Button - NEW FEATURE */}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AIIcon />}
              onClick={() => setAiUploadDialogOpen(true)}
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                background: 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)',
                }
              }}
              size={isTablet ? 'small' : 'medium'}
            >
              Nahrát s AI
            </Button>
            
            {/* Create ABO Button - only show when there are ready invoices */}
            {statistics.readyForPayment > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<BusinessIcon />}
                onClick={() => {
                  if (businessUnitFilter === 'all') {
                    showError('Musíte vybrat firmu pro vytvoření platebního souboru. Použijte filtr účetní jednotky.');
                    return;
                  }
                  setCreateABODialogOpen(true);
                }}
                title={businessUnitFilter === 'all' ? 'Vyberte firmu pro vytvoření platebního souboru' : 'Vytvořit platební soubor'}
                sx={{ minWidth: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.75rem', md: '0.8rem' } }}
                size={isTablet ? 'small' : 'medium'}
              >
                Vytvořit ABO
              </Button>
            )}
            <ExportButton
              currentFilters={{
                status: { type: 'specific', values: statusFilter && statusFilter !== 'all' ? [statusFilter] : [] },
                businessUnit: { type: 'specific', ids: businessUnitFilter && businessUnitFilter !== 'all' ? [parseInt(businessUnitFilter)] : [] },
                dateRange: { 
                  type: 'custom', 
                  customDates: { 
                    from: (dateFrom && dateFrom instanceof Date && !isNaN(dateFrom.getTime())) ? format(dateFrom, 'yyyy-MM-dd') : undefined, 
                    to: (dateTo && dateTo instanceof Date && !isNaN(dateTo.getTime())) ? format(dateTo, 'yyyy-MM-dd') : undefined 
                  } 
                },
                search: { type: searchTerm ? 'text' : 'none', text: searchTerm || '', fields: ['invoiceNumber', 'supplierName', 'customerName'] }
              }}
              onExportComplete={() => {
                // Refresh data with current filters (non-debounced)
                loadInvoices();
              }}
            />
          </Box>
        </Box>
        {/* Show bulk actions on tablets and desktop, hide only on small mobile */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center', 
          gap: { md: 1, lg: 2 }, 
          flexWrap: 'wrap',
          width: '100%',
          justifyContent: { md: 'flex-start', lg: 'flex-end' }
        }}>
          {/* Archive by Status - Always visible on tablets and desktop */}
          <Button
            variant="outlined"
            color="warning"
            onClick={handleArchiveByStatusClick}
            startIcon={<ArchiveIcon />}
            size={isTablet ? 'small' : 'medium'}
            sx={{ fontSize: { md: '0.75rem', lg: '0.875rem' } }}
          >
            Archivovat podle stavu
          </Button>

          {/* Bulk actions - Only visible when invoices are selected */}
          {selectedInvoices.length > 0 && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: { md: '0.75rem', lg: '0.875rem' } }}>
                Vybráno: {selectedInvoices.length}
              </Typography>
              <Button
                variant="contained"
                color="success"
                onClick={handleBulkISDOCExport}
                startIcon={<DownloadIcon />}
                size={isTablet ? 'small' : 'medium'}
                disabled={bulkExportLoading}
                sx={{ fontSize: { md: '0.75rem', lg: '0.875rem' } }}
              >
                {bulkExportLoading ? 'Exportuji...' : 'Export ISDOC'}
              </Button>
              <Button
                variant="outlined"
                color="info"
                onClick={() => setBulkStatusDialogOpen(true)}
                startIcon={<EditIcon />}
                size={isTablet ? 'small' : 'medium'}
                sx={{ fontSize: { md: '0.75rem', lg: '0.875rem' } }}
              >
                Změnit status
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleBulkArchiveClick}
                startIcon={<ArchiveIcon />}
                size={isTablet ? 'small' : 'medium'}
                sx={{ fontSize: { md: '0.75rem', lg: '0.875rem' } }}
              >
                {czechTranslations.invoices.actions.bulkArchive}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleBulkDeleteClick}
                startIcon={<DeleteIcon />}
                size={isTablet ? 'small' : 'medium'}
                sx={{ fontSize: { md: '0.75rem', lg: '0.875rem' } }}
              >
            Smazat vybrané
          </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, sm: 1.5, md: 2 }, mb: { xs: 2, md: 2, lg: 3 }, maxWidth: '100%' }}>
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} alignItems="center">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              fullWidth
              label="Hledat"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">Všechny</MenuItem>
                {Object.keys(czechTranslations.invoices.status).map((key) => (
                  <MenuItem key={key} value={key}>{getStatusLabel(key)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Účetní jednotka</InputLabel>
              <Select
                value={businessUnitFilter}
                onChange={(e) => setBusinessUnitFilter(e.target.value)}
                label="Účetní jednotka"
              >
                <MenuItem value="all">Všechny účetní jednotky</MenuItem>
                <MenuItem value="unassigned">Nepřiřazené</MenuItem>
                {safeBusinessUnits.map((bu) => (
                  <MenuItem key={bu.id} value={bu.id.toString()}>
                    {bu.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
              <DatePicker
                label="Od data"
                value={dateFrom}
                onChange={setDateFrom}
                slotProps={{ textField: { fullWidth: true, size: isMobile ? "small" : "medium" } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
              <DatePicker
                label="Do data"
                value={dateTo}
                onChange={setDateTo}
                slotProps={{ textField: { fullWidth: true, size: isMobile ? "small" : "medium" } }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 2, md: 2, lg: 3 }} sx={{ mb: { xs: 2, md: 2, lg: 3 }, width: '100%', margin: 0 }}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Celkem faktur
              </Typography>
              <Typography variant="h4" color="primary.main">
                {statistics.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Nové
              </Typography>
              <Typography variant="h4" color="default.main">
                {statistics.new}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Čekající na zpracování
              </Typography>
              <Typography variant="h4" color="warning.main">
                {statistics.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Připravené k platbě
              </Typography>
              <Typography variant="h4" color="primary.main">
                {statistics.readyForPayment}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Celková hodnota
              </Typography>
              <Typography variant="h4" color="success.main">
                {statistics.totalValue.toLocaleString('cs-CZ')} Kč
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Optimized Invoice Table - Safari performance optimized with virtualization */}
      <Box className="invoice-table-container optimized-table">
        <OptimizedInvoiceTable
        data={optimizedInvoices}
        loading={loading}
        pagination={pagination}
        selectedInvoices={selectedInvoices}
        onSelectionChange={setSelectedInvoices}
        onPaginationChange={(page, pageSize) => handlePaginationModelChange({ page: page - 1, pageSize })}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onAssignCompany={handleAssignCompany}
        onSortChange={handleSortChange}
        onOpenNotes={handleOpenNotesDialog}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Smazat fakturu
        </DialogTitle>
        <DialogContent>
          <Typography>
            Opravdu chcete smazat fakturu "{invoiceToDelete?.invoiceNumber}"?
            Tato akce je nevratná.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Zrušit
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Smazat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={handleBulkDeleteCancel}
        aria-labelledby="bulk-delete-dialog-title"
        aria-describedby="bulk-delete-dialog-description"
      >
        <DialogTitle id="bulk-delete-dialog-title">
          Smazat vybrané faktury
        </DialogTitle>
        <DialogContent>
          <Typography>
            Opravdu chcete smazat {selectedInvoices.length} vybraných faktur?
            Tato akce je nevratná.
          </Typography>
          {user?.role !== 'admin' && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Poznámka: Můžete smazat pouze své vlastní faktury.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel} color="primary">
            Zrušit
          </Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained">
            Smazat ({selectedInvoices.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog
        open={bulkStatusDialogOpen}
        onClose={handleBulkStatusCancel}
        aria-labelledby="bulk-status-dialog-title"
        aria-describedby="bulk-status-dialog-description"
      >
        <DialogTitle id="bulk-status-dialog-title">
          Aktualizovat status vybraných faktur
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {user?.role !== 'admin' 
              ? 'Můžete aktualizovat pouze své vlastní faktury.'
              : 'Můžete aktualizovat všechny faktury.'
            }
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Nový status</InputLabel>
            <Select
              value={bulkStatusToUpdate}
              onChange={(e) => setBulkStatusToUpdate(e.target.value)}
              label="Nový status"
            >
              {Object.keys(czechTranslations.invoices.status).map((key) => (
                <MenuItem key={key} value={key}>{getStatusLabel(key)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkStatusCancel} color="primary">
            Zrušit
          </Button>
          <Button onClick={handleBulkStatusChange} color="info" variant="contained">
            Aktualizovat ({selectedInvoices.length})
          </Button>
        </DialogActions>
      </Dialog>

      {AlertComponent}
      
      {/* Create ABO Dialog */}
      <Dialog open={createABODialogOpen} onClose={handleCloseABODialog} maxWidth="md" fullWidth>
        <DialogTitle>Vytvořit nový platební soubor</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Účetní jednotka</InputLabel>
                <Select
                  value={businessUnitFilter}
                  onChange={(e) => setBusinessUnitFilter(e.target.value)}
                  label="Účetní jednotka"
                  error={businessUnitFilter === 'all'}
                >
                  <MenuItem value="all" disabled>Vyberte účetní jednotku</MenuItem>
                  {businessUnits.map((businessUnit) => (
                    <MenuItem key={businessUnit.id} value={businessUnit.id}>
                      {businessUnit.name} ({businessUnit.ico})
                    </MenuItem>
                  ))}
                </Select>
                {businessUnitFilter === 'all' && (
                  <FormHelperText error>
                    Musíte vybrat účetní jednotku pro vytvoření platebního souboru
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Banka a způsob odeslání</InputLabel>
                <Select
                  value={selectedABOBankType}
                  onChange={(e) => setSelectedABOBankType(e.target.value)}
                  label="Banka a způsob odeslání"
                >
                  <MenuItem value="FIO_ABO">FIO Banka - ABO soubor</MenuItem>
                  <MenuItem value="FIO_API">FIO Banka - API (přímé odeslání)</MenuItem>
                  <MenuItem value="CSOB">ČSOB - ABO soubor</MenuItem>
                  <MenuItem value="KB">Komerční banka - ABO soubor</MenuItem>
                  <MenuItem value="MONETA">MONETA Money Bank - ABO soubor</MenuItem>
                  <MenuItem value="AIRBANK">Air Bank - ABO soubor</MenuItem>
                  <MenuItem value="RAIFFEISEN">Raiffeisenbank - ABO soubor</MenuItem>
                  <MenuItem value="UNICREDIT">UniCredit Bank - ABO soubor</MenuItem>
                  <MenuItem value="SPORITELNA">Česká spořitelna - ABO soubor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Submit now option - only show for FIO API */}
            {selectedABOBankType === 'FIO_API' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={submitNow}
                      onChange={(e) => setSubmitNow(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Odeslat ihned do FIO banky"
                />
              </Grid>
            )}
            
            {/* Bank account selection - only show when submitNow is true */}
            {submitNow && selectedABOBankType === 'FIO_API' && (
              <Grid item xs={12}>
                {loadingImmediateBankAccounts ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography>Načítání bankovních účtů...</Typography>
                  </Box>
                ) : availableImmediateBankAccounts.length > 0 ? (
                  <FormControl fullWidth>
                    <InputLabel>Bankovní účet *</InputLabel>
                    <Select
                      value={immediateBankAccountId || ''}
                      onChange={(e) => setImmediateBankAccountId(e.target.value as number)}
                      label="Bankovní účet *"
                      required
                    >
                      {availableImmediateBankAccounts.map((account: any) => (
                        <MenuItem key={account.id} value={account.id}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {account.account_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {account.account_number} / {account.bank_code}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Vyberte účet, ze kterého budou provedeny platby
                    </FormHelperText>
                  </FormControl>
                ) : (
                  <Alert severity="warning">
                    Pro tuto firmu nejsou nakonfigurovány žádné FIO bankovní účty s API tokenem.
                    <br />
                    <Button 
                      size="small" 
                      sx={{ mt: 1 }}
                      onClick={() => {
                        navigate('/business-units');
                        handleCloseABODialog();
                      }}
                    >
                      Nakonfigurovat bankovní účty
                    </Button>
                  </Alert>
                )}
              </Grid>
            )}
            
            {/* Warning about immediate submission */}
            {submitNow && selectedABOBankType === 'FIO_API' && availableImmediateBankAccounts.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2" fontWeight="medium">
                    ⚠️ Okamžité odeslání do FIO banky
                  </Typography>
                  <Typography variant="body2">
                    Platby budou odeslány do FIO banky ihned po vytvoření souboru. 
                    Ujistěte se, že máte dostatek prostředků na účtu {immediateBankAccountId ? 
                      availableImmediateBankAccounts.find(acc => acc.id === immediateBankAccountId)?.account_number : ''
                    }.
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Vyberte faktury pro platební soubor
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedABOInvoices.length === invoices.filter(i => i.status === 'ready_for_payment').length && invoices.filter(i => i.status === 'ready_for_payment').length > 0}
                      indeterminate={selectedABOInvoices.length > 0 && selectedABOInvoices.length < invoices.filter(i => i.status === 'ready_for_payment').length}
                      onChange={handleSelectAllReadyInvoices}
                    />
                  }
                  label="Vybrat všechny připravené"
                />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Vybráno: {selectedABOInvoices.length} z {invoices.filter(i => i.status === 'ready_for_payment').length}
                </Typography>
              </Box>
              <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                <List>
                  {invoices.filter(i => i.status === 'ready_for_payment').map((invoice) => (
                    <ListItem key={invoice.id} dense>
                      <Checkbox
                        checked={selectedABOInvoices.includes(invoice.id)}
                        onChange={() => handleABOInvoiceToggle(invoice.id)}
                      />
                      <ListItemText
                        primary={invoice.invoiceNumber}
                        secondary={`${invoice.supplierName} - ${invoice.totalAmount?.toLocaleString('cs-CZ')} Kč`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={getStatusLabel(invoice.status)}
                          size="small"
                          color="primary"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
          
          {creatingABO && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Vytvářím ABO soubor...
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseABODialog} disabled={creatingABO}>
            Zrušit
          </Button>
          <Button
            onClick={handleCreateABO}
            disabled={
              selectedABOInvoices.length === 0 || 
              creatingABO || 
              (submitNow && selectedABOBankType === 'FIO_API' && !immediateBankAccountId) ||
              (submitNow && selectedABOBankType === 'FIO_API' && availableImmediateBankAccounts.length === 0)
            }
            variant="contained"
          >
            {submitNow && selectedABOBankType === 'FIO_API' 
              ? 'Vytvořit a odeslat' 
              : 'Vytvořit platební soubor'
            }
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Company Assignment Dialog */}
      <CompanyAssignmentDialog
        open={companyAssignmentDialogOpen}
        onClose={() => {
          setCompanyAssignmentDialogOpen(false);
          setInvoiceToAssignCompany(null);
        }}
        invoice={invoiceToAssignCompany}
        onAssignmentComplete={() => {
          loadInvoices();
        }}
      />

      {/* Internal Notes Dialog */}
      <InternalNotesDialog
        open={notesDialogOpen}
        onClose={handleCloseNotesDialog}
        invoice={selectedInvoiceForNotes}
        onSave={handleSaveInternalNotes}
      />

      {/* AI Invoice Upload Dialog */}
      <AIInvoiceUpload
        open={aiUploadDialogOpen}
        onClose={() => setAiUploadDialogOpen(false)}
        onSuccess={() => {
          showSuccess('Faktura byla úspěšně vytvořena pomocí AI');
          loadInvoices();
          setAiUploadDialogOpen(false);
        }}
      />

      {/* Archive Single Invoice Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
        aria-labelledby="archive-dialog-title"
      >
        <DialogTitle id="archive-dialog-title">
          {czechTranslations.invoices.messages.confirmArchive.title}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {czechTranslations.invoices.messages.confirmArchive.message}
          </Typography>
          {invoiceToArchive && (
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
              Faktura: {invoiceToArchive.invoiceNumber}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel}>
            {czechTranslations.invoices.messages.confirmArchive.cancel}
          </Button>
          <Button onClick={handleArchiveConfirm} variant="contained" color="warning">
            {czechTranslations.invoices.messages.confirmArchive.confirm}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Archive Confirmation Dialog */}
      <Dialog
        open={bulkArchiveDialogOpen}
        onClose={handleBulkArchiveCancel}
        aria-labelledby="bulk-archive-dialog-title"
      >
        <DialogTitle id="bulk-archive-dialog-title">
          {czechTranslations.invoices.messages.confirmBulkArchive.title}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {czechTranslations.invoices.messages.confirmBulkArchive.message(selectedInvoices.length)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkArchiveCancel}>
            {czechTranslations.invoices.messages.confirmBulkArchive.cancel}
          </Button>
          <Button onClick={handleBulkArchiveConfirm} variant="contained" color="warning">
            {czechTranslations.invoices.messages.confirmBulkArchive.confirm}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive By Status Dialog */}
      <Dialog
        open={archiveByStatusDialogOpen}
        onClose={handleArchiveByStatusCancel}
        aria-labelledby="archive-by-status-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="archive-by-status-dialog-title">
          📦 Archivovat faktury podle stavu
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Vyberte stav faktur, které chcete archivovat. Všechny faktury s vybraným stavem budou archivovány.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Stav</InputLabel>
            <Select
              value={statusToArchive}
              onChange={(e) => setStatusToArchive(e.target.value)}
              label="Stav"
            >
              <MenuItem value="new">{czechTranslations.invoices.status.new}</MenuItem>
              <MenuItem value="pending">{czechTranslations.invoices.status.pending}</MenuItem>
              <MenuItem value="reviewed">{czechTranslations.invoices.status.reviewed}</MenuItem>
              <MenuItem value="ready_for_payment">{czechTranslations.invoices.status.ready_for_payment}</MenuItem>
              <MenuItem value="sent_to_bank">{czechTranslations.invoices.status.sent_to_bank}</MenuItem>
              <MenuItem value="paid">{czechTranslations.invoices.status.paid}</MenuItem>
              <MenuItem value="paid_in_cash">{czechTranslations.invoices.status.paid_in_cash}</MenuItem>
              <MenuItem value="rejected">{czechTranslations.invoices.status.rejected}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveByStatusCancel}>
            Zrušit
          </Button>
          <Button 
            onClick={handleArchiveByStatusConfirm} 
            variant="contained" 
            color="warning"
            disabled={!statusToArchive}
          >
            Archivovat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invoices; 